<template>
  <div class="login-box mt-7">
    <div class="login-box-header">
      <h1>{{ $t('walletPass.get-started') }}</h1>
    </div>
    <v-card class="login-box-body">
      <v-card-text v-if="!isSubmitted">
        <div class="text-center">{{ $t('walletPass.form-description') }}</div>
        <br/>
      </v-card-text>
      <ValidationObserver ref="walletPassForm" v-slot="{ invalid }">

        <v-form @submit.prevent="login">
          <v-card-text class="wallet-pass-form">
            <v-row v-if="isSubmitted">
              <div class="mx-auto">
                <v-icon>{{ icons.mdiAccount }}</v-icon>
                {{ `${walletForm.first_name} ${walletForm.last_name}` }}
              </div>
            </v-row>
            <template v-else>
              <ValidationProvider ref="firstName" name="firstName" vid="firstName" rules="required" slim v-slot="{ errors, invalid }">
                <v-text-field
                  name="firstName"
                  v-model="walletForm.first_name"
                  :error-messages="showIdentifierErrorMessages ? errors[0] : []"
                  type="text"
                  :label="$t('walletPass.enter-first-name')"
                  prepend-icon="person"
                  @blur="showIdentifierErrorMessages = true"
                >
                </v-text-field>
              </ValidationProvider>
              <ValidationProvider ref="lastName" name="lastName" vid="lastName" rules="required" slim v-slot="{ errors, invalid }">
                <v-text-field
                  name="lastName"
                  v-model="walletForm.last_name"
                  :error-messages="showIdentifierErrorMessages ? errors[0] : []"
                  type="text"
                  :label="$t('walletPass.enter-last-name')"
                  prepend-icon="person"
                  @blur="showIdentifierErrorMessages = true"
                >
                </v-text-field>
              </ValidationProvider>
            </template>

            <ValidationProvider ref="identifier" name="identifier" vid="identifier" slim rules="required|email" v-slot="{ errors, invalid }">
              <v-row v-if="isSubmitted">
                <div class="mx-auto identifier">
                  <v-icon>{{ icons.mdiEmail }}</v-icon>
                  {{ walletForm.email }}
                </div>
              </v-row>
              <v-text-field
                v-else
                name="identifier"
                v-model="walletForm.email"
                :error-messages="showIdentifierErrorMessages ? errors[0] : []"
                type="text"
                :label="$t('walletPass.enter-email')"
                prepend-icon="email"
                @blur="showIdentifierErrorMessages = true">
              </v-text-field>
            </ValidationProvider>

            <br/>

            <div v-if="isSubmitted">
              <br/>
              <div v-if="passCreationFailed">
                <div>
                  <v-btn type="button" color="primary" @click="generatePass" :loading="generateLoading">
                    {{$t('walletPass.retry')}}
                  </v-btn>
                </div>
              </div>
              <div v-else>
                <p>{{ $t('walletPass.submit-message') }}</p>
                <v-btn type="button" @click="generatePass" icon dark class="ignore-primary">
                  <img :src="imageUrl" alt="Add to wallet" />
                </v-btn>
              </div>
            </div>
            <div v-else>
              <div>
                <v-btn type="submit" color="primary" :disabled="invalid" :loading="submitLoading" block>
                  {{$t('walletPass.submit')}}
                </v-btn>
              </div>
            </div>

          </v-card-text>

        </v-form>
      </ValidationObserver>
      <template v-if="errorMessage">
        <br/>
        <div class="text-center">{{ errorMessage }}</div>
      </template>
    </v-card>
  </div>
</template>

<script>
import {mapGetters, mapState, mapActions} from 'vuex'
import log from 'loglevel'
import _ from 'lodash';
import {v4 as uuidv4} from 'uuid'
import DeviceDetector from "device-detector-js"
import {storage} from '@/store'
import {mdiAccount, mdiEmail} from '@mdi/js';

export default {
  data() {
      return {
        walletForm: {
          name: '',
          email: '',
          walletPassURL: ''
        },
        errorMessage: '',
        showIdentifierErrorMessages: false,
        submitLoading: false,
        generateLoading: false,
        isSubmitted: false,
        passCreationFailed: false,
        icons: {mdiAccount, mdiEmail}
      };
    },
    async mounted() {
      if (storage.getItem('walletPassUser')) {
        const walletPassUser = JSON.parse(storage.getItem('walletPassUser'))
        this.walletForm = {
          ...walletPassUser
        }
        this.isSubmitted = true
        if (!this.walletForm.walletPassURL) {
          this.passCreationFailed = true
          this.errorMessage = this.$t('walletPass.pass-creation-failed')
        }
        await this.userLogin()
      }
    },
    computed: {
      ...mapGetters([
        'authSettings',
        'extensionSettings',
        'client',
        'device',
        'installPWAEvent'
      ]),
      ...mapState(['webAppConfig']),
      client_id() {
        return this.$store.state.client_id
      },
      signinIdentifierRule() {
        return 'required|email'
      },
      baseUrl() {
        return process.env.BASE_URL;
      },
      deviceOS() {
        const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;

        if (/iPhone|iPad|iPod/.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
          return "ios";
        } else {
          return "android";
        }
      },
      langIso() {
        return (this.$root.$i18n.locale.toLowerCase() === 'de') ? 'de' : 'en';
      },
      imageUrl() {
        return `${this.baseUrl}img/wallet-pass/${this.langIso}_${this.deviceOS}.svg`
      }
    },
    methods: {
      ...mapActions([
        'tosScreen',
        'signUp',
        'userAuthenticate',
        'checkIdentifier',
        'deviceLogin',
        'updateUserProfile',
        'registerPass'
      ]),
      downloadPkPass(url, fileName) {
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      },
      async generatePass() {
        try {
          this.generateLoading = true
          const registerPassResponse = await this.registerPass({
            device: this.deviceOS
          })
          if (this.deviceOS === 'ios') {
            this.walletForm.walletPassURL = URL.createObjectURL(registerPassResponse);
            await this.downloadPkPass(this.walletForm.walletPassURL, "wallet_pass.pkpass")
          } else {
            this.walletForm.walletPassURL = _.get(registerPassResponse, 'wallet_pass_link')
            window.open(this.walletForm.walletPassURL, '_blank')
          }
          // update storage
          let profile = JSON.parse(storage.getItem('walletPassUser'))
          profile.walletPassURL = this.walletForm.walletPassURL
          storage.removeItem('walletPassUser')
          storage.setItem('walletPassUser', JSON.stringify(profile))
          this.passCreationFailed = false
          this.errorMessage = ''
        } catch (error) {
          log.error(error)
          this.errorMessage = this.$t('walletPass.pass-creation-failed')
          this.passCreationFailed = true
        } finally {
          this.generateLoading = false
          this.tosScreen({show: false})
        }
      },
      async getDeviceInfo() {
        const uniqueId = uuidv4()
        const device = new DeviceDetector().parse(window.navigator.userAgent)
        const deviceInfo = {
          device_user_id: uniqueId,
          device_info: {
            user_agent: window.navigator.userAgent,
            app_version: window.navigator.appVersion.split(' ')[0],
            system_name: window.navigator.platform,
            system_version: device.os.version,
            device_model: device.device.model,
            device_type: device.device.type
          }
        }
        return deviceInfo
      },
      // Handle the error response
      handleErrorCredentialResponse(error) {
        log.error('error: ', error)
        this.tosScreen({show: false})
        this.showLoginFailedDialog = true
        this.loading = false
      },
      async login() {
        this.tosScreen({show: true, acceptAction: this.performLogin, rejectAction: this.rejectTOS})
      },
      async performLogin() {
        this.loading = true
        try {
          await this.signIn()
        } catch (error) {
          this.handleErrorCredentialResponse(error)
        } finally {
          this.loading = false
        }
      },
      rejectTOS() {
        this.tosScreen({show: false})
      },
      reset() {
        this.walletForm.email = "";
        this.walletForm.first_name = "";
        this.walletForm.last_name = "";
        this.errorMessage = "";
        setTimeout(() => {
          this.showIdentifierErrorMessages = false;
        }, 10);
      },
      async signIn() {
        this.submitLoading = true
        this.errorMessage = ''
        try {
          const response = await this.checkIdentifier({
            identifier: this.walletForm.email
          })
          if (response.error) {
            throw response
          } else if (!response.isRegistered) {
            const profile = {
              email: this.walletForm.email,
              first_name: this.walletForm.first_name,
              last_name: this.walletForm.last_name
            }
            await this.userLogin()
            await this.updateUserProfile(profile)
            storage.setItem('walletPassUser', JSON.stringify(profile))
          }
          this.isSubmitted = true
        } catch (error) {
          log.error(error)

          if (_.has(error, 'code') && error.code === 'USER_ALREADY_EXISTS') {
            this.errorMessage = this.$t('registerScreen.registerErrorUserAlreadyExists')
          } else if (_.has(error, 'code') && error.code === 'PASS_CREATION_FAILED') {
            this.errorMessage = this.$t('walletPass.pass-creation-failed')
            this.isSubmitted = true
          } else {
            this.errorMessage = this.$t('walletPass.submission-fail')
          }
        } finally {
          this.tosScreen({show: false})
          this.submitLoading = false
        }
      },
      async userLogin() {
        try {
          const deviceinfo = await this.getDeviceInfo()
          await this.deviceLogin(deviceinfo)
        } catch (error) {
          throw error
        }
      },

    },
}
</script>

<style scoped lang="scss">
  .wallet-pass-form {
    padding: 0;
    .identifier {
      margin-top: 10px;
    }
  }
  .login-box {
    .login-box-body {
      text-align: center;
    }
  }
</style>
